import Backbone from 'backbone';
import _ from 'underscore';
import ko from 'knockout';
import config from '@bingads-webui-component-legacy/config/index';
import TrackingTemplate from '@bingads-webui-component-legacy/upgraded-url/view/trackingTemplate';
import UrlTestTool from '@bingads-webui-component-legacy/upgraded-url/view/urlTestTool';
import { CustomParametersView, FinalUrlSuffixView } from '@bingads-webui-campaign/upgrade-url';
import template from '@bingads-webui-component-legacy/upgraded-url/template/urlOptionsV2.pug';
import ViewModel from '@bingads-webui-component-legacy/upgraded-url/viewmodel/urlOptionsV2';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!component/upgraded-url');

export default class View extends Backbone.View {
  get className() {
    return 'url-options';
  }

  initialize({
    placeholderEnabled = false,
    trackingTemplate = undefined,
    finalUrlSuffix = undefined,
    customParameters = undefined,
    entityType = 'Ad',
    showUurlTestTool = false,
    isForVNext = false,
    activityLogger,
    readOnly = false,
  } = {}) {
    const permissions = config.get('Permissions') || {};

    this.activityLogger = activityLogger;

    const finalUrlSuffixPhase1EnabledEntities = ['campaign', 'adgroup', 'keyword'];
    const finalUrlSuffixPhase2EnabledEntities = ['ad', 'audience'];
    // to be conversative, enabling to only these two extensions known to use the advanced URL.
    const finalUrlSuffixPhase3EnabledEntities = ['imageadextension', 'sitelink2adextension'];
    const finalUrlSuffixPhase4EnabledEntities = ['actionadextension', 'priceadextension'];
    const entity = entityType ? entityType.toLowerCase() : '';

    this.isFinalURLSuffixEnabled = finalUrlSuffixPhase1EnabledEntities.indexOf(entity) >= 0 ||
      finalUrlSuffixPhase2EnabledEntities.indexOf(entity) >= 0 ||
      (permissions.IsFinalURLSuffixPhase3Enabled && finalUrlSuffixPhase3EnabledEntities.indexOf(entity) >= 0) ||
      (permissions.IsFinalURLSuffixPhase4Enabled && finalUrlSuffixPhase4EnabledEntities.indexOf(entity) >= 0);

    this.viewModel = new ViewModel({ entityType, isFinalURLSuffixEnabled: this.isFinalURLSuffixEnabled });

    this.trackingTemplate = new TrackingTemplate({
      placeholderEnabled,
      trackingTemplate,
      isForVNext,
      readOnly,
    });

    this.trackingTemplate.on('templateChanged', () => {
      this.logActivity('tracking template change');
    });

    this.finalUrlSuffix = new FinalUrlSuffixView({
      finalUrlSuffix,
      i18n,
      showPlaceholder: true,
      readOnly,
    });

    this.finalUrlSuffix.on('finalUrlSuffix:updated', () => {
      this.logActivity('final url suffix change');
    });

    this.customParams = new CustomParametersView({
      customParameters,
      placeholderEnabled,
      permissions: config.get('Permissions') || {},
      i18n,
      entityType,
      readOnly,
    });

    this.showUurlTestTool = showUurlTestTool;
    if (this.showUurlTestTool) {
      const urlTestToolOptions = { isForVNext };

      this.urlTestTool = new UrlTestTool(urlTestToolOptions);
      this.urlTestTool.on('testUurl', (fn, originalView) => {
        this.logActivity('click test url');
        this.trigger('testUurl', fn, originalView);
      });
    }

    this.logCustomParamsChange = _.once(() => {
      this.logActivity('custom parameter change');
    });

    return this;
  }

  logActivity(message) {
    if (this.activityLogger) {
      this.activityLogger.signal(message, 'url options');
    }
  }

  customParamsChanged() {
    this.logCustomParamsChange();
  }

  get events() {
    return {
      'click .expand-url-options': 'expand',
      'change input.custom-param__value-field, input.custom-param__key-field': 'customParamsChanged',
    };
  }

  isValid() {
    const isValid = this.trackingTemplate.isValid() && this.customParams.isValid() && (!this.isFinalURLSuffixEnabled || this.finalUrlSuffix.isValid());

    if (!isValid) {
      if (!this.trackingTemplate.isValid()) {
        this.logActivity('tracking template is not valid');
      }

      if (!this.customParams.isValid()) {
        this.logActivity('custom parameter is not valid');
      }

      if (this.isFinalURLSuffixEnabled && !this.finalUrlSuffix.isValid()) {
        this.logActivity('final url suffix is not valid');
      }
    }

    return isValid;
  }

  render() {
    this.$el.html(template({
      i18n,
    }));

    ko.applyBindings(this.viewModel, this.el);

    this.$('.tracking-template__container').empty().append(this.trackingTemplate.render().el);
    this.$('.custom-paramters__container').empty().append(this.customParams.render().el);

    this.viewModel.trackingTemplate(this.getTrackingTemplate());
    this.viewModel.parameters(this.getUrlCustomParameters(true));

    if (this.isFinalURLSuffixEnabled) {
      this.$('.final-url-suffix__container').empty().append(this.finalUrlSuffix.render().el);
      this.viewModel.finalUrlSuffix(this.getFinalUrlSuffix());
    }

    if (this.showUurlTestTool) {
      this.$('.uurl-test-tool__container').empty().append(this.urlTestTool.render().el);
    }

    return this;
  }

  expand() {
    const state = this.viewModel.isExpanded();

    this.viewModel.isExpanded(!state);
    if (!this.viewModel.isExpanded()) {
      this.viewModel.trackingTemplate(this.getTrackingTemplate());
      if (this.isFinalURLSuffixEnabled) {
        this.viewModel.finalUrlSuffix(this.getFinalUrlSuffix());
      }
      this.viewModel.parameters(this.getUrlCustomParameters(true));
    }
    this.logActivity(`url options toggle: ${this.viewModel.isExpanded()}`);
  }

  toObject() {
    return this.isFinalURLSuffixEnabled ? {
      'UrlCustomParameters': this.getUrlCustomParameters(true),
      'TrackingUrlTemplate': this.getTrackingTemplate(),
      'FinalUrlSuffix': this.getFinalUrlSuffix(),
    } : {
      'UrlCustomParameters': this.getUrlCustomParameters(true),
      'TrackingUrlTemplate': this.getTrackingTemplate(),
    };
  }

  clearApiErrors() {
    this.trackingTemplate.ko_model.trackingTemplateErrors([]);
    this.customParams.viewModel.parametersError([]);
    if (this.isFinalURLSuffixEnabled) {
      this.finalUrlSuffix.viewModel.externalErrors([]);
    }
  }

  getUrlCustomParameters(isFlatten) {
    return this.customParams.getValues(isFlatten);
  }

  getTrackingTemplate() {
    return this.trackingTemplate.ko_model.trackingTemplate();
  }

  getFinalUrlSuffix() {
    return this.finalUrlSuffix.getFinalUrlSuffix();
  }

  handleApiErrors(errors) {
    let hasUrlOptionErrors = false;

    if (!_.isEmpty(errors.TrackingUrlTemplate)) {
      this.trackingTemplate.ko_model.trackingTemplateErrors(errors.TrackingUrlTemplate);
      hasUrlOptionErrors = true;
    }

    if (this.isFinalURLSuffixEnabled && !_.isEmpty(errors.FinalUrlSuffix)) {
      this.finalUrlSuffix.viewModel.externalErrors(errors.FinalUrlSuffix);
      hasUrlOptionErrors = true;
    }

    if (!_.isEmpty(errors.UrlCustomParameters)) {
      this.customParams.viewModel.parametersError(errors.UrlCustomParameters);
      hasUrlOptionErrors = true;
    }

    if (hasUrlOptionErrors) {
      this.viewModel.isExpanded(true);
    }
  }
}
